var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import tinycolor from "tinycolor2";
import pluralize from 'pluralize';
import { addDays, addMonths, addWeeks, addYears, endOfDay, endOfMonth, endOfWeek, endOfYear, isDate, isValid, startOfDay, startOfMonth, startOfWeek, startOfYear, subDays, subMonths, subWeeks, subYears } from "date-fns";
import { ComponentType } from '@types';
/**
 * @export
 * True when the account runs as an air-gapped/offline deployment. Reads the
 * server-injected `is_offline` hidden input on each call. Internet-only
 * features must check this.
 *
 * @example
 * ```
 * if (isOfflineDeployment()) return null;
 * ```
 */
export function isOfflineDeployment() {
    var _a;
    try {
        return ((_a = document.getElementById('is_offline')) === null || _a === void 0 ? void 0 : _a.value) === '1';
    }
    catch (_b) {
        return false;
    }
}
const CDN_ORIGINS = [
    ['https://uxp.iviva.com', ''],
    ['https://static.iviva.com', '/static'],
];
/**
 * @export
 * Maps a CDN URL (uxp.iviva.com / static.iviva.com) to the offline-resources
 * mirror on an offline deployment. Online, and for any other URL, it returns
 * the URL unchanged. Same mapping as the server's RewriteForOffline.
 *
 * @example
 * ```
 * const logo = resolveResourceUrl('https://static.iviva.com/iviva-logo-dark.png');
 * // offline: '/Resources/UXP/offline-resources/static/iviva-logo-dark.png'
 * ```
 */
export function resolveResourceUrl(url) {
    var _a;
    if (!url || !isOfflineDeployment())
        return url;
    let base = '/Resources/UXP/offline-resources';
    try {
        base = ((_a = document.getElementById('offline_resource_path')) === null || _a === void 0 ? void 0 : _a.value) || base;
    }
    catch ( /* no document - keep the default */_b) { /* no document - keep the default */ }
    base = base.replace(/\/$/, '');
    for (const [origin, prefix] of CDN_ORIGINS) {
        if (url === origin)
            return base + prefix;
        if (url.startsWith(origin + '/'))
            return base + prefix + url.slice(origin.length);
    }
    return url;
}
// brand assets come from the CDN normally; air-gapped deployments serve them
// from the offline-resources mirror (uxp-cli downloads the files into the
// mirror's static/ folder). Module init runs after DOM parse (deferred
// scripts), so the hidden inputs are readable here.
export const IvivaLogoDark = resolveResourceUrl("https://static.iviva.com/iviva-logo-dark.png");
export const IvivaLogoLight = resolveResourceUrl("https://static.iviva.com/iviva-logo-light.png");
export const LucyLogoIcon = resolveResourceUrl('https://static.iviva.com/lucy/logos/lucy-canvas-square.svg');
/**
 * Gets the appropriate logo based on theme context and header background color
 *
 * @example
 * ```
 * const logo = getLogo(context);
 * ```
 */
/**
 * URL of the signed-in user's site picture, or null when there is none. Prefers the
 * backend-resolved URL (profile image, with legacy attachment fallback baked in); falls back to
 * the attachment URL from the legacy key for older backends.
 */
export function getUserSiteImageUrl(context) {
    if (hasValue(context === null || context === void 0 ? void 0 : context.userSiteImageUrl))
        return context.userSiteImageUrl;
    if (hasValue(context === null || context === void 0 ? void 0 : context.userSiteImageKey))
        return `/Attachments/Download?AttachmentKey=${context.userSiteImageKey}`;
    return null;
}
export function getLogo(context) {
    var _a, _b;
    if (hasValue((_a = context === null || context === void 0 ? void 0 : context.theme) === null || _a === void 0 ? void 0 : _a.logo))
        return context.theme.logo;
    const headerBGColor = tinycolor(((_b = context === null || context === void 0 ? void 0 : context.theme) === null || _b === void 0 ? void 0 : _b.portalHeaderBGColor) || '#2B3EEA');
    return tinycolor.readability(headerBGColor, '#0065FF') > tinycolor.readability(headerBGColor, '#fff') ? IvivaLogoLight : IvivaLogoDark;
}
/**
 * Checks if a script with the given ID exists in the DOM
 *
 * @example
 * ```
 * if (!isScriptExists('my-script')) {
 *   // Load the script
 * }
 * ```
 */
export function isScriptExists(id, remove, src) {
    if (id) {
        const element = document.getElementById(id);
        if (element) {
            if (!remove)
                return true;
            element.remove();
        }
    }
    // A tag with the same src may have been injected by another loader (e.g. the
    // shell's background bundle loader injects tags without ids). Re-executing an
    // app bundle double-registers its components, so treat it as already loaded.
    if (src && document.querySelector(`script[src="${CSS.escape(src)}"]`))
        return true;
    return false;
}
/**
 * @export
 * Loads a JavaScript file dynamically with optional callback
 *
 * @example
 * ```
 * loadScriptFile('/js/chart.js', () => {
 *   console.log('Chart library loaded');
 * }, 'chart-script');
 * ```
 */
export function loadScriptFile(path, callback, id, replaceIfExist) {
    // Exit early if script already exists (by id, or same src injected by another
    // loader) and shouldn't be replaced.
    // ponytail: on a src match the tag may still be loading and the callback fires
    // early; late registrations are covered by the uxp:component-registered event.
    if (isScriptExists(id, replaceIfExist, path)) {
        callback === null || callback === void 0 ? void 0 : callback();
        return;
    }
    const script = document.createElement('script');
    if (id)
        script.id = id;
    script.src = path;
    script.async = true; // Make script loading non-blocking
    script.onload = () => callback === null || callback === void 0 ? void 0 : callback();
    script.onerror = (e) => callback === null || callback === void 0 ? void 0 : callback(e);
    document.head.appendChild(script);
}
/**
 * @export
 * Loads a JavaScript file asynchronously using promises
 *
 * @example
 * ```
 * try {
 *   await loadScriptFileAsync('/js/chart.js', 'chart-script');
 *   console.log('Script loaded successfully');
 * } catch (error) {
 *   console.error('Failed to load script');
 * }
 * ```
 */
export function loadScriptFileAsync(path, id, replaceIfExist) {
    // The widget designer bundle is the global external host app (loaded via __wdscript__ /
    // UXP.Designer.Script CDN), not a DB module — fetching it via /api/UXP/module 400s. Treat it
    // as already loaded so callers fall through to lookupComponent.
    if (id === 'uxp-widget-designer-bundle')
        return Promise.resolve();
    return new Promise((resolve, reject) => {
        loadScriptFile(path, (error) => error ? reject(error) : resolve(), id, replaceIfExist);
    });
}
// Shares one in-flight load across concurrent callers so a dashboard with many
// tiles of the same module fetches/executes its bundle once, not once per tile.
// ponytail: cache is per page-session; a mid-session module version bump would be
// skipped, which never happens within a single page load.
const moduleLoadCache = new Map();
/**
 * @export
 * Loads all scripts associated with a module ID
 *
 * @example
 * ```
 * await loadAllScriptsByModuleId(context, 'dashboard-module');
 * ```
 */
export function loadAllScriptsByModuleId(context, moduleId) {
    var _a;
    // Designer bundle is a global external (see loadScriptFileAsync) — never fetch it.
    if (!moduleId || moduleId === 'uxp-widget-designer-bundle')
        return Promise.resolve();
    const cached = moduleLoadCache.get(moduleId);
    if (cached)
        return cached;
    const scripts = ((_a = context === null || context === void 0 ? void 0 : context.scriptFiles) === null || _a === void 0 ? void 0 : _a[moduleId]) || [];
    if (scripts.length === 0)
        return Promise.resolve();
    const load = () => __awaiter(this, void 0, void 0, function* () {
        try {
            yield Promise.all(scripts.map(script => {
                const id = `${moduleId}-${simpleHash(script, 'script-')}`;
                return loadScriptFileAsync(script, id, true);
            }));
        }
        catch (error) {
            moduleLoadCache.delete(moduleId); // allow retry after a failed load
            console.error(`Failed to load scripts for module: ${moduleId}`, error);
            throw new Error(`Failed to load one or more scripts for module: ${moduleId}`);
        }
    });
    const promise = load();
    moduleLoadCache.set(moduleId, promise);
    return promise;
}
/**
 * Evaluates JavaScript code in a specific variable scope
 *
 * @example
 * ```
 * const vars = { x: 10, y: 20 };
 * const fn = evalInScope(vars, 'return x + y;');
 * const result = fn(); // returns 30
 * ```
 */
export function evalInScope(vars, code) {
    let script = '';
    let keys = Object.keys(vars);
    for (let i = 0; i < keys.length; i++) {
        let key = keys[i];
        script += `const ${key} = vars['${key}'];\n`;
    }
    script += '\n' + code;
    let f = Function('vars', script);
    return () => f(vars);
}
/**
 * @export
 * Generates a UUID string
 *
 * @example
 * ```
 * const id = generateUUID();
 * console.log(id); // "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx"
 * ```
 */
export function generateUUID() {
    var d = new Date().getTime();
    var uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c == 'x' ? r : (r & 0x7 | 0x8)).toString(16);
    });
    return uuid;
}
/**
 * scripts and modules
 */
const moduleScriptBasePath = '/api/UXP/module';
/**
 * @export
 * Generates a script path from module ID
 *
 * @example
 * ```
 * const path = generateScriptPathFromModuleId('dashboard-module', 'https://app.example.com');
 * ```
 */
export function generateScriptPathFromModuleId(moduleId, lucyUrl) {
    const base = hasValue(lucyUrl) ? trimSlash(lucyUrl) : '';
    return `${base}${moduleScriptBasePath}?id=${moduleId}`;
}
/**
 * @export
 * Normalizes a module script URL to a relative path by stripping any leading origin
 * (e.g. "http://account:5001/api/UXP/module?id=X" → "/api/UXP/module?id=X").
 * Non-module URLs (e.g. S3 URLs) are returned unchanged.
 */
export function normalizeModuleScriptUrl(url) {
    if (!url)
        return url;
    const idx = url.indexOf(moduleScriptBasePath);
    if (idx > 0)
        return url.slice(idx); // strip leading http://host:port
    return url;
}
/**
 * @export
 * Extracts module ID from script path
 *
 * @example
 * ```
 * const moduleId = getModuleIdFromScriptPath('/api/UXP/module?id=dashboard-module');
 * ```
 */
export function getModuleIdFromScriptPath(scriptPath) {
    if (scriptPath.startsWith(moduleScriptBasePath)) {
        const id = scriptPath.replace(`${moduleScriptBasePath}?id=`, '');
        return id;
    }
    return null;
}
/**
 * @export
 * Gets module ID from widget configuration
 *
 * @example
 * ```
 * const moduleId = getModuleIdFromWidget(widget);
 * ```
 */
export function getModuleIdFromWidget(widget) {
    if (hasValue(widget.usecaseId))
        return widget.usecaseId;
    else {
        let parts = widget.id.split('/');
        return parts[0];
    }
}
/**
 * @export
 * Returns true if the given id belongs to a widget.
 * Handles both 3-part legacy IDs ("moduleId/widget/componentId") and
 * 2-part unified IDs ("moduleId/componentId") from the unified component registry.
 */
export function isWidget(id) {
    var _a, _b;
    const parts = id === null || id === void 0 ? void 0 : id.split('/');
    // 3-part legacy format: moduleId/widget/componentId
    if ((parts === null || parts === void 0 ? void 0 : parts.length) === 3)
        return parts[1] === 'widget';
    // 2-part unified format: consult the registry for the modes field
    if ((parts === null || parts === void 0 ? void 0 : parts.length) === 2) {
        const comp = lookupComponent(id);
        if (comp)
            return (_b = (_a = comp.modes) === null || _a === void 0 ? void 0 : _a.includes('widget')) !== null && _b !== void 0 ? _b : true;
        // Script not yet loaded — trust the caller's tab context (Widgets tab only reaches here)
        return true;
    }
    return false;
}
/**
 * @export
 * Creates a simple hash from a string with optional prefix
 *
 * @example
 * ```
 * const hash = simpleHash('my-string', 'prefix-');
 * console.log(hash); // "prefix-123456789"
 * ```
 */
export function simpleHash(str, prefix = 'hash-', options) {
    let hash = 0;
    if (str.length === 0)
        return null;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    const { base = 10, ignorePrefix = false } = options || {};
    const finalHash = Math.abs(hash).toString(base);
    return ignorePrefix ? finalHash : `${prefix}${finalHash}`;
}
/**
 * Gets script ID from script path
 *
 * @example
 * ```
 * const scriptId = getScriptId('/api/UXP/module?id=dashboard-module');
 * ```
 */
export function getScriptId(scriptPath) {
    const moduleId = getModuleIdFromScriptPath(scriptPath);
    if (hasValue(moduleId))
        return moduleId;
    return simpleHash(scriptPath, 'script-');
}
/**
 * @export
 * Component types enumeration
 */
/**
 * Normalize any ID form to a comparable 2-part key by stripping the middle type segment.
 *   moduleId/widget/compId    →  moduleId/compId
 *   moduleId/ui/compId        →  moduleId/compId
 *   moduleId/component/compId →  moduleId/compId
 *   moduleId/compId            →  moduleId/compId  (unchanged)
 *
 * Called once per item at registration (stored as ComponentDefinition.normalizedId).
 * Called once per lookup on the search ID only — never per-item at lookup time.
 */
export function normalizeComponentId(id) {
    const parts = id.toLowerCase().split('/');
    return parts.length === 3 ? `${parts[0]}/${parts[2]}` : id.toLowerCase();
}
/**
 * Generates a 2-part component ID: moduleId/componentId
 *
 * @example
 * ```
 * const id = generateComponentId('iviva-system-app', 'chart');
 * // Returns: 'iviva-system-app/chart'
 * ```
 */
export function generateComponentId(moduleId, componentId) {
    if (hasValue(moduleId) && hasValue(componentId)) {
        return `${moduleId}/${componentId}`.toLowerCase();
    }
    return (componentId === null || componentId === void 0 ? void 0 : componentId.toLowerCase()) || null;
}
/**
 * @export
 * Parses a component ID string into its constituent parts.
 * Accepts both 3-part (moduleId/type/compId) and 2-part (moduleId/compId) formats.
 *
 * @example
 * ```
 * parseComponentId('iviva-system-app/widget/chart');
 * // Returns: { moduleId: 'iviva-system-app', componentId: 'chart', type: ComponentType.Widget }
 * ```
 */
export function parseComponentId(id) {
    if (!hasValue(id))
        return null;
    const parts = id.split('/');
    if (parts.length === 3) {
        const [moduleId, typeStr, componentId] = parts;
        if (!hasValue(moduleId) || !hasValue(componentId))
            return null;
        const type = Object.values(ComponentType).find((t) => t === typeStr.toLowerCase());
        return { moduleId, componentId, type };
    }
    if (parts.length === 2) {
        const [moduleId, componentId] = parts;
        if (!hasValue(moduleId) || !hasValue(componentId))
            return null;
        return { moduleId, componentId };
    }
    return null;
}
/**
 * Get all registered components, optionally filtered by mode.
 */
export function getComponents(mode) {
    const all = window.Components || [];
    return mode ? all.filter(c => { var _a; return (_a = c.modes) === null || _a === void 0 ? void 0 : _a.includes(mode); }) : all;
}
/**
 * Look up a registered component by any ID form (3-part or 2-part).
 * Normalizes the search ID once, then compares against pre-computed ComponentDefinition.normalizedId.
 *
 * @example
 * ```
 * lookupComponent('iviva-system-app/widget/chart');
 * lookupComponent('iviva-system-app/ui/general-settings-view');
 * lookupComponent('iviva-system-app/component/my-comp');
 * ```
 * @export
 */
export function lookupComponent(id) {
    var _a;
    if (!hasValue(id))
        return null;
    const normalized = normalizeComponentId(id);
    return (_a = getComponents().find(c => c.normalizedId === normalized)) !== null && _a !== void 0 ? _a : null;
}
/**
 * Looks up a configured view by ID and optional tags
 *
 * @example
 * ```
 * const view = lookupConfiguredView(views, 'dashboard-view', { theme: 'dark' });
 * ```
 */
export function lookupConfiguredView(views, id, tags) {
    var _a;
    const filtered = (views || [])
        .filter(v => v.id == id)
        // calculate score
        .map(item => ({
        score: Object.entries(tags || {}).filter(([k, v]) => { var _a; return ((_a = item.tags) === null || _a === void 0 ? void 0 : _a[k]) === v; }).length,
        page: item
    }))
        //sort by score 
        .sort((a, b) => b.score - a.score);
    // get the first
    return ((_a = filtered === null || filtered === void 0 ? void 0 : filtered[0]) === null || _a === void 0 ? void 0 : _a.page) || null;
}
/**
 * Widgets
 */
/**
 *
 * Filters unique widgets from an array of widget instances
 *
 * @example
 * ```
 * const uniqueWidgets = filterUniqueWidgets(widgetInstances);
 * ```
 */
export function filterUniqueWidgets(widgets) {
    let ids = {};
    let uniqueWidgets = widgets.filter((widget) => {
        const id = widget.id;
        const name = getWidgetName(widget);
        if (!hasValue(id) || !hasValue(name))
            return false;
        if (!ids[id])
            ids[id] = [];
        if (ids[id].indexOf(name) == -1) {
            ids[id].push(name);
            return true;
        }
        return false;
    });
    return uniqueWidgets;
}
/**
 * Gets the display name of a widget
 *
 * @example
 * ```
 * const name = getWidgetName(widget);
 * ```
 */
export function getWidgetName(widget) {
    var _a, _b;
    let _widget_name = widget.name || "";
    if (!_widget_name || _widget_name.trim().length == 0) {
        if (widget.title && ((_a = widget.title) === null || _a === void 0 ? void 0 : _a.trim().length) > 0) {
            _widget_name = widget.title;
        }
        else if (widget.label && ((_b = widget.label) === null || _b === void 0 ? void 0 : _b.trim().length) > 0) {
            _widget_name = widget.label;
        }
    }
    return _widget_name;
}
/**
 * Sidebar items
 */
/**
 * @deprecated registerLink is a no-op in V5. Navigation is config-based.
 * window.SidebarLinks is never populated — this always returned undefined.
 */
export function getSidebarItemsInDev() {
    return [];
}
/**
 * @deprecated registerLink and registerMenuItem are no-ops in V5.
 * Navigation is now config-based (Configuration.yml / AccountNavigationLinks).
 * These functions always return null.
 */
export function lookupSidebarItem(_id) { return null; }
export function lookupMenuItem(_id) { return null; }
/* extracted from underscore/lodash */
/**
 * Throttles function execution to prevent excessive calls
 *
 * @example
 * ```
 * const throttledScroll = throttle(() => {
 *   console.log('Scroll event');
 * }, 100);
 * window.addEventListener('scroll', throttledScroll);
 * ```
 */
export function throttle(func, wait, options) {
    var context, args, result;
    var timeout = null;
    var previous = 0;
    if (!options)
        options = {};
    var later = function () {
        previous = options.leading === false ? 0 : Date.now();
        timeout = null;
        result = func.apply(context, args);
        if (!timeout)
            context = args = null;
    };
    return function () {
        var now = Date.now();
        if (!previous && options.leading === false)
            previous = now;
        var remaining = wait - (now - previous);
        context = this;
        args = arguments;
        if (remaining <= 0 || remaining > wait) {
            if (timeout) {
                clearTimeout(timeout);
                timeout = null;
            }
            previous = now;
            result = func.apply(context, args);
            if (!timeout)
                context = args = null;
        }
        else if (!timeout && options.trailing !== false) {
            timeout = setTimeout(later, remaining);
        }
        return result;
    };
}
;
/**
 * @export
 * Removes trailing slashes from a string.
 *
 * @example
 * ```ts
 * const cleanUrl = trimSlash('https://example.com/');
 * console.log(cleanUrl); // "https://example.com"
 *
 * const path = trimSlash('/api/v1/');
 * console.log(path); // "/api/v1"
 * ```
 */
export function trimSlash(s) {
    return s === null || s === void 0 ? void 0 : s.replace(/\/$/, "");
}
/**
 * @export
 * Removes trailing slashes from a string.
 *
 * @example
 * ```ts
 * const cleanUrl = trimTrailingSlash('https://example.com/');
 * console.log(cleanUrl); // "https://example.com"
 *
 * const path = trimTrailingSlash('/api/v1/');
 * console.log(path); // "/api/v1"
 * ```
 */
export function trimTrailingSlash(s) {
    return trimSlash(s);
}
/**
 * @export
 * Removes leading slashes from a string.
 *
 * @example
 * ```ts
 * const cleanPath = trimLeadingSlash('/api/v1/users');
 * console.log(cleanPath); // "api/v1/users"
 *
 * const url = trimLeadingSlash('https://example.com');
 * console.log(url); // "https://example.com"
 * ```
 */
export function trimLeadingSlash(s) {
    return s === null || s === void 0 ? void 0 : s.replace(/^\/+/, "");
}
/**
 * Finds the first item in an array that matches the predicate function
 *
 * @example
 * ```
 * const users = [{id: 1, name: 'John'}, {id: 2, name: 'Jane'}];
 * const user = findFirst(users, u => u.id === 2);
 * ```
 */
export function findFirst(haystack, needle) {
    let items = haystack.filter((x) => needle(x));
    if (items.length == 0) {
        return null;
    }
    return items[0];
}
/**
 * Generates an acronym from a string
 *
 * @example
 * ```
 * const acronym = getAcronym('User Experience Platform', 3);
 * console.log(acronym); // "UEP"
 * ```
 */
export const getAcronym = (str, limit) => {
    var matches = str.match(/\b(\w)/g);
    if (!matches) {
        console.log('unable_to_get_the_letters_from_string', str);
    }
    var acronym = (matches || []).join('');
    if (!!limit && limit > 0) {
        acronym = acronym.slice(0, limit);
    }
    return acronym;
};
// random color generation
const HSVtoRGB = (h, s, v) => {
    var r, g, b, i, f, p, q, t;
    i = Math.floor(h * 6);
    f = h * 6 - i;
    p = v * (1 - s);
    q = v * (1 - f * s);
    t = v * (1 - (1 - f) * s);
    switch (i % 6) {
        case 0:
            r = v, g = t, b = p;
            break;
        case 1:
            r = q, g = v, b = p;
            break;
        case 2:
            r = p, g = v, b = t;
            break;
        case 3:
            r = p, g = q, b = v;
            break;
        case 4:
            r = t, g = p, b = v;
            break;
        case 5:
            r = v, g = p, b = q;
            break;
    }
    return {
        r: Math.round(r * 255),
        g: Math.round(g * 255),
        b: Math.round(b * 255)
    };
};
/**
 * Generates a random RGB color using golden ratio for pleasing aesthetics
 *
 * @example
 * ```
 * const color = randomRGBColor();
 * console.log(color); // "rgba(123, 45, 67)"
 * ```
 */
export const randomRGBColor = () => {
    let h = Math.random();
    let goldenRatioConjugate = 0.618033988749895;
    h += goldenRatioConjugate;
    h %= 1;
    let RGB = HSVtoRGB(h, 0.5, 0.95);
    let RGBString = "rgba(" + RGB.r + "," + RGB.g + "," + RGB.b + ")";
    return RGBString;
};
/**
 * Converts a string to a consistent hex color
 *
 * @example
 * ```
 * const color = strToColor('John Doe');
 * console.log(color); // "#A1B2C3"
 * ```
 */
export const strToColor = (string) => {
    // get hash
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    // generate color
    let c = (hash & 0x00ffffff).toString(16).toUpperCase();
    let hexCode = "#" + "00000".substring(0, 6 - c.length) + c;
    return hexCode;
};
/**
 * Formats a date to ISO format string (YYYY/MM/DD)
 *
 * @example
 * ```
 * const formatted = dateToISOFormat(new Date());
 * console.log(formatted); // "2023/12/25"
 * ```
 */
export const dateToISOFormat = (date) => {
    if (!date)
        return;
    let Y = date.getFullYear();
    let _M = date.getMonth() + 1;
    let M = "00".substring(0, (2 - _M.toString().length)) + _M;
    let _D = date.getDate();
    let D = "00".substring(0, (2 - _D.toString().length)) + _D;
    let _date = Y + "/" + M + "/" + D;
    return _date;
};
/**
 * Splits a string into two approximately equal parts
 *
 * @example
 * ```
 * const [first, second] = splitStringToTwo('Hello World Example');
 * console.log(first, second); // "Hello " "World Example"
 * ```
 */
export function splitStringToTwo(str) {
    const parts = str.split(/(\s+)/);
    const length = Math.floor(parts.length / 2);
    return [parts.slice(0, length).join(''), parts.slice(length).join('')];
}
/**
 * @export
 * Copies text to the system clipboard
 *
 * @example
 * ```
 * copyToClipboard('Hello World');
 * ```
 */
export function copyToClipboard(text) {
    if (navigator.clipboard && window.isSecureContext) {
        // Modern async API
        return navigator.clipboard.writeText(text).catch((err) => {
            console.error("Clipboard copy failed:", err);
        });
    }
    else {
        // Fallback for older browsers
        const elem = document.createElement("textarea");
        elem.value = text;
        elem.style.position = "fixed";
        elem.style.left = "-9999px";
        document.body.appendChild(elem);
        elem.focus();
        elem.select();
        try {
            document.execCommand("copy");
        }
        catch (err) {
            console.error("Fallback copy failed:", err);
        }
        document.body.removeChild(elem);
    }
}
/**
 * Rounds a number to specified decimal places
 *
 * @example
 * ```
 * const rounded = _round(3.14159, 2);
 * console.log(rounded); // 3.14
 * ```
 */
export function _round(value, precision) {
    var multiplier = Math.pow(10, precision || 0);
    return Math.round(value * multiplier) / multiplier;
}
/**
 * Generates a random number within a specified range
 *
 * @example
 * ```
 * const randomNum = getRandomArbitraryNumberInRange(1, 100);
 * ```
 */
export function getRandomArbitraryNumberInRange(min, max) {
    return Math.random() * (max - min) + min;
}
/**
 * @export
 * Debounces function execution to prevent excessive calls
 *
 * @example
 * ```
 * const debouncedSearch = debounce((query) => {
 *   console.log('Searching for:', query);
 * }, 300);
 * ```
 */
export function debounce(func, waitMilliseconds = 50, options = { isImmediate: false }) {
    let timeoutId;
    return function (...args) {
        const doLater = () => {
            timeoutId = undefined;
            if (!options.isImmediate)
                func(...args);
        };
        const shouldCallNow = options.isImmediate && timeoutId === undefined;
        if (timeoutId !== undefined)
            clearTimeout(timeoutId);
        timeoutId = setTimeout(doLater, waitMilliseconds);
        if (shouldCallNow)
            func(...args);
    };
}
/**
 * Checks if a string is a valid image URL
 *
 * @example
 * ```
 * const isImage = isImageUrl('https://example.com/image.png');
 * console.log(isImage); // true
 * ```
 */
export function isImageUrl(iconStr) {
    if (!iconStr)
        return false;
    return /^(https?:\/\/|data:|\/)/.test(iconStr) || /\.(svg|png|jpe?g|gif|webp)$/i.test(iconStr);
}
/**
 * @export
 * @deprecated Moved to @icon/utils - this is a re-export for backward compatibility
 * Checks if a string is a FontAwesome icon class
 *
 * @example
 * ```
 * const isFAIcon = isFontawesomeIcon('fas fa-home');
 * console.log(isFAIcon); // true
 * ```
 */
export { isFontawesomeIcon } from "@icon/utils";
/**
 * @export
 * @deprecated Moved to @icon/utils - this is a re-export for backward compatibility
 * Converts a FontAwesome string to IconProp format
 *
 * @example
 * ```
 * const iconProp = getFontAwesomeIcon('fas fa-home');
 * ```
 */
export { getFontAwesomeIcon } from "@icon/utils";
/**
 * Checks if a query object is a Data Explorer query
 *
 * @example
 * ```
 * const isDE = isDataExplorerQuery(queryObject);
 * ```
 */
export function isDataExplorerQuery(query) {
    return (!!query && (query === null || query === void 0 ? void 0 : query.hasOwnProperty('sources')) && (query === null || query === void 0 ? void 0 : query.hasOwnProperty('steps')));
}
/**
 * @export
 * Checks if a value has a meaningful value (not null, undefined, or empty)
 *
 * @example
 * ```
 * if (hasValue(userInput)) {
 *   console.log('User provided input');
 * }
 * ```
 */
export function hasValue(value, allowZero, allowNegative) {
    if (allowZero && typeof value == "number" && value == 0)
        return true;
    if (allowNegative && typeof value == "number" && value < 0)
        return true;
    if (!value)
        return false;
    switch (typeof value) {
        case 'string':
            return (value === null || value === void 0 ? void 0 : value.trim().length) > 0;
        case 'number':
            return value > 0;
        default:
            return true;
    }
}
/**
 * @export
 * Shallow-compares two objects one level deep (keys + `Object.is` on each value).
 * Use as a `React.memo` comparator when a component receives a props bag that is
 * rebuilt as a fresh object literal every render (identical entries → equal).
 *
 * @example
 * ```
 * export default memo(MyView, (a, b) => shallowEqual(a.props, b.props));
 * ```
 */
export function shallowEqual(a, b) {
    if (a === b)
        return true;
    if (!a || !b)
        return false;
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length)
        return false;
    for (const k of ka) {
        if (!Object.is(a[k], b[k]))
            return false;
    }
    return true;
}
/**
 * Calculates header background, text color, and logo color based on header color
 *
 * @example
 * ```
 * const colors = getHeaderAndTitleColor('#2B3EEA');
 * console.log(colors.textColor); // "#ffffff"
 * ```
 */
export function getHeaderAndTitleColor(headerColor) {
    let headerBG = null;
    let textColor = null;
    let logoColor = 'dark';
    if (headerColor) {
        const hbg = tinycolor(headerColor);
        let bgAlpha = hbg.getAlpha();
        // if (bgAlpha === 1) hbg.setAlpha(0.8)
        // console.log(hbg.getAlpha());
        headerBG = hbg.toHex8String();
        const txtColor = tinycolor.mostReadable(hbg, ['#424242', '#fff']);
        textColor = txtColor.toHex8String();
        logoColor = tinycolor.readability(hbg, '#4d4d4d') > tinycolor.readability(hbg, '#fff') ? 'dark' : 'light';
    }
    return { headerBG, textColor, logoColor };
}
/**
 *
 * Gets style configurations from context
 *
 * @example
 * ```
 * const styles = getStyleConfigurations(context);
 * ```
 */
export function getStyleConfigurations(context) {
    var _a;
    let configurations = {};
    // console.log('configurations', configurations);
    const backgroundImage = hasValue(configurations === null || configurations === void 0 ? void 0 : configurations.BackgroundImage) ? configurations.BackgroundImage : null;
    const blurBackground = hasValue(configurations === null || configurations === void 0 ? void 0 : configurations.BlurBackground) ? configurations.BlurBackground === '1' : false;
    const headerBackgroundColor = hasValue(configurations === null || configurations === void 0 ? void 0 : configurations.HeaderBackgroundColor) ? configurations.HeaderBackgroundColor : ((_a = context === null || context === void 0 ? void 0 : context.theme) === null || _a === void 0 ? void 0 : _a.portalHeaderBGColor) || null;
    const { headerBG, textColor, logoColor } = getHeaderAndTitleColor(headerBackgroundColor);
    return {
        bgImage: backgroundImage,
        blugBg: blurBackground,
        headerBg: headerBG,
        headerTxt: textColor,
        logoColor
    };
}
/**
 * Generates CSS properties for sidebar styling with gradients
 *
 * @example
 * ```
 * const style = getSidebarColorStyle('#ff0000', '#00ff00', '45deg');
 * ```
 */
export function getSidebarColorStyle(color, color2, angle) {
    if (!color)
        return {};
    let style = {};
    if (!color2 || color == color2) {
        style.backgroundColor = color;
    }
    else {
        style.backgroundImage = `linear-gradient(${angle},${color},${color2})`;
    }
    let bgColor = tinycolor(color);
    style.color = bgColor.isDark() ? '#fff' : '#424242';
    return style;
}
/**
 * Safely parses JSON string with fallback to default value
 *
 * @example
 * ```
 * const data = tryParseJSON('{"name": "John"}', {});
 * ```
 */
export function tryParseJSON(x, def) {
    var _a;
    const { parsed, error } = parseJson(x);
    if (!!error)
        return def !== null && def !== void 0 ? def : null;
    return (_a = parsed !== null && parsed !== void 0 ? parsed : def) !== null && _a !== void 0 ? _a : null;
}
/**
 * Safely parses JSON string and returns parsed data or error
 *
 * @example
 * ```
 * const { parsed, error } = parseJson('{"name": "John"}');
 * if (error) {
 *   console.error(error);
 * } else {
 *   console.log(parsed.name);
 * }
 * ```
 */
export function parseJson(jsonString) {
    try {
        const parsed = JSON.parse(jsonString);
        return { parsed, error: null };
    }
    catch (e) {
        const error = e instanceof Error ? e.message : String(e);
        return { parsed: null, error };
    }
}
/**
 *
 * Extracts tracking properties from widgets for analytics
 *
 * @example
 * ```
 * const props = extracPropsToTrack(widgets);
 * ```
 */
export function extracPropsToTrack(widgets) {
    return widgets.map(w => ({
        id: w.id,
        name: w.name,
        category: w.category || 'user-defined',
        usecaseId: w.usecaseId || null,
        usecaseName: w.usecaseName || null
    }));
}
/**
 * @export
 * Converts a string to a URL-friendly slug: lowercase letters, numbers and single hyphens.
 * Accented letters become plain letters, `&` becomes `and`, apostrophes are dropped and any
 * other run of characters (spaces, underscores, symbols) becomes one hyphen.
 *
 * @param string - the text to convert
 * @param options - `{ typing: true }` while the user is still typing: a trailing hyphen is kept so the next
 *                  word can follow. Leave it out for the final value, which has no leading or trailing hyphen.
 *                  A plain boolean is accepted for older callers and gives the final value.
 *
 * @example
 * ```
 * getUrlFriendlyString('Hello World & More');   // "hello-world-and-more"
 * getUrlFriendlyString('Café_Menu ');           // "cafe-menu"
 * getUrlFriendlyString('Café_Menu ', { typing: true });  // "cafe-menu-"
 * ```
 */
export const getUrlFriendlyString = (string, options) => {
    // a boolean was the old, unused removeSlashes flag: callers passing true still get the final value
    const typing = typeof options === 'object' && !!(options === null || options === void 0 ? void 0 : options.typing);
    const slug = String(string !== null && string !== void 0 ? string : '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // strip accents: é -> e
        .toLowerCase()
        .replace(/['’]/g, '') // drop apostrophes: it's -> its
        .replace(/&/g, '-and-')
        .replace(/[^a-z0-9]+/g, '-') // anything else, underscores included, -> one hyphen
        .replace(/^-+/, '');
    return typing ? slug : slug.replace(/-+$/, '');
};
/**
 *
 * Tracks analytics events with error handling
 *
 * @example
 * ```
 * trackAnalytics(context, 'widget_clicked', { widgetId: 'chart-1' });
 * ```
 */
export function trackAnalytics(context, event, data) {
    var _a;
    try {
        (_a = context.trackAnalytics) === null || _a === void 0 ? void 0 : _a.call(context, event, data);
    }
    catch (e) {
        console.log('Unable to track analytics. Error: ', e);
    }
}
/**
 * @export
 * Performs a text search with case sensitivity option
 *
 * @example
 * ```
 * const found = textSearch('Hello World', 'world', true);
 * console.log(found); // true
 * ```
 */
export function textSearch(value, query, ignoreCase = true) {
    const _value = toStr(value);
    if (ignoreCase) {
        return (_value === null || _value === void 0 ? void 0 : _value.toLowerCase().indexOf(query === null || query === void 0 ? void 0 : query.toLowerCase())) !== -1;
    }
    else {
        return (_value === null || _value === void 0 ? void 0 : _value.indexOf(query)) !== -1;
    }
}
/**
 * @export
 * Converts any value to string representation
 *
 * @example
 * ```
 * const str = toStr(123);
 * console.log(str); // "123"
 * ```
 */
export function toStr(value, def) {
    if (value == undefined)
        return def || '';
    const _type = typeof value;
    if (_type === 'string')
        return value;
    if (_type === 'object') {
        if (isDate(value) && isValid(value)) {
            return value.toISOString();
        }
        return JSON.stringify(value);
    }
    if (_type === 'boolean') {
        return value ? 'true' : 'false';
    }
    return String(value);
}
/**
 * @export
 * Converts any value to boolean
 *
 * @example
 * ```
 * const bool = toBool('true');
 * console.log(bool); // true
 * ```
 */
export function toBool(value, def) {
    const _type = typeof value;
    if (_type == 'boolean')
        return value;
    if (_type == 'string') {
        return (value === null || value === void 0 ? void 0 : value.toLowerCase()) == 'true' || value == '1';
    }
    if (_type == 'number')
        return value == 1;
    return def || false;
}
/**
 * @export
 * Converts any value to number with fallback
 *
 * @example
 * ```
 * const num = toNum('123.45', 0);
 * console.log(num); // 123.45
 * ```
 */
export function toNum(value, def) {
    // Check for null/undefined/empty before conversion because Number(val) will convert anything to 0 if value if not a number or cannot convert to number 
    if (value === null || value === undefined || (typeof value == 'string' && !hasValue(value))) {
        return arguments.length > 1 ? def : 0;
    }
    const _type = typeof value;
    const _n = (_type === 'number') ? value : Number(value);
    if (isNaN(_n) || !isFinite(_n)) {
        return (arguments === null || arguments === void 0 ? void 0 : arguments.length) > 1 ? def : 0;
    }
    return _n;
}
/**
 * @export
 * Rounds a number to specified decimal places
 *
 * @example
 * ```
 * const rounded = roundOff(3.14159, 2);
 * console.log(rounded); // 3.14
 * ```
 */
export function roundOff(num, decimalPlaces = 2) {
    const factor = Math.pow(10, decimalPlaces);
    return Math.round(num * factor) / factor;
}
/**
 * @export
 * Converts any value to JSON object with type inference from value or default
 *
 * @example
 * ```
 * const obj = toJSON('{"name": "John"}', {});
 * console.log(obj.name); // "John"
 *
 * // Type is inferred from the value when it's typed
 * const links = toJSON(comparison?.dbLinks, []); // NavigationLink[]
 *
 * // Type is inferred from default when value is string
 * const parsed = toJSON('{"key": "value"}', {});
 * ```
 */
export function toJSON(value, def) {
    const _type = typeof value;
    const defaultVal = (typeof def == undefined ? {} : def);
    if (_type == 'object')
        return value;
    if (_type == 'string') {
        return tryParseJSON(value, defaultVal);
    }
    return defaultVal;
}
/**
 * @export
 * Converts string, number, or Date to Date object
 *
 * @example
 * ```
 * const date = toDate('2023-12-25');
 * console.log(date); // Date object
 * ```
 */
export function toDate(d) {
    if (typeof d == 'string' && isRelativeDate(d))
        d = parseRelativeDate(d);
    if (typeof d == 'number' || typeof d == 'string')
        d = new Date(d);
    if (isValid(d))
        return d;
    return null;
}
/**
 * @export
 * Converts word to singular form
 *
 * @example
 * ```
 * const singular = toSingular('users');
 * console.log(singular); // "user"
 * ```
 */
export function toSingular(word) {
    return pluralize.singular(word);
}
/**
 * @export
 * Converts word to plural form
 *
 * @example
 * ```
 * const plural = toPlural('user');
 * console.log(plural); // "users"
 * ```
 */
export function toPlural(word) {
    return pluralize.plural(word);
}
/**
 * @export
 * convert text tp kebab case
 */
export function toKebabCase(str) {
    return str
        .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
        .replace(/([A-Z])([A-Z][a-z])/g, '$1-$2')
        .toLowerCase();
}
const relativeDateRegex = /^([+-]?\d+)([dwmy])(\|?(start|end)?)?$/i;
/**
 * @export
 * Checks if a string represents a relative date
 *
 * @example
 * ```
 * const isRelative = isRelativeDate('-3d');
 * console.log(isRelative); // true
 * ```
 */
export function isRelativeDate(date) {
    if (typeof date !== 'string')
        return false;
    // Regex to match relative date formats (e.g., -3d, +2w, 5m, 1y)
    // List of edge cases (e.g., startOfDay, endOfMonth)
    const edgeCases = [
        "startOfDay",
        "endOfDay",
        "startOfWeek",
        "endOfWeek",
        "startOfMonth",
        "endOfMonth",
        "startOfYear",
        "endOfYear"
    ];
    // Check if it matches the relative date regex or is one of the edge cases
    return relativeDateRegex.test(date) || edgeCases.includes(date);
}
/**
 * @export
 * Parses a relative date string into an actual Date object
 *
 * @example
 * ```
 * const date = parseRelativeDate('-3d'); // 3 days ago
 * const startDate = parseRelativeDate('0d|start'); // start of today
 * ```
 */
export function parseRelativeDate(relativeDate, baseDate = new Date()) {
    // Handle edge cases first
    if (!relativeDateRegex.test(relativeDate)) {
        const edgeCases = {
            startOfDay: startOfDay(baseDate),
            endOfDay: endOfDay(baseDate),
            startOfWeek: startOfWeek(baseDate),
            endOfWeek: endOfWeek(baseDate),
            startOfMonth: startOfMonth(baseDate),
            endOfMonth: endOfMonth(baseDate),
            startOfYear: startOfYear(baseDate),
            endOfYear: endOfYear(baseDate),
        };
        if (relativeDate in edgeCases) {
            return edgeCases[relativeDate];
        }
        return null; // Invalid input
    }
    // Parse relative date
    const match = relativeDate.match(relativeDateRegex);
    if (!match) {
        return null; // Invalid input
    }
    const [, amount, unit, , boundary] = match; // Extract matched groups
    const value = parseInt(amount, 10); // Convert amount to integer
    let calculatedDate;
    switch (unit.toLowerCase()) {
        case "d":
            calculatedDate = value >= 0 ? addDays(baseDate, value) : subDays(baseDate, Math.abs(value));
            break;
        case "w":
            calculatedDate = value >= 0 ? addWeeks(baseDate, value) : subWeeks(baseDate, Math.abs(value));
            break;
        case "m":
            calculatedDate = value >= 0 ? addMonths(baseDate, value) : subMonths(baseDate, Math.abs(value));
            break;
        case "y":
            calculatedDate = value >= 0 ? addYears(baseDate, value) : subYears(baseDate, Math.abs(value));
            break;
        default:
            return null;
    }
    // Apply the boundary (start or end of period)
    if (boundary) {
        switch (unit.toLowerCase()) {
            case "d":
                return boundary.toLowerCase() === "start" ? startOfDay(calculatedDate) : endOfDay(calculatedDate);
            case "w":
                return boundary.toLowerCase() === "start" ? startOfWeek(calculatedDate) : endOfWeek(calculatedDate);
            case "m":
                return boundary.toLowerCase() === "start" ? startOfMonth(calculatedDate) : endOfMonth(calculatedDate);
            case "y":
                return boundary.toLowerCase() === "start" ? startOfYear(calculatedDate) : endOfYear(calculatedDate);
            default:
                return null;
        }
    }
    return calculatedDate;
}
/**
 * @export
 * Gets human-readable label for relative date strings
 *
 * @example
 * ```
 * const label = getRelativeDateLabel('-3d');
 * console.log(label); // "3 Days Before"
 * ```
 */
export function getRelativeDateLabel(relativeDate) {
    // Mapping for edge cases with human-readable labels
    const edgeCaseLabels = {
        startOfDay: "Start of Today",
        endOfDay: "End of Today",
        startOfWeek: "Start of Week",
        endOfWeek: "End of Week",
        startOfMonth: "Start of Month",
        endOfMonth: "End of Month",
        startOfYear: "Start of Year",
        endOfYear: "End of Year"
    };
    // Handle edge cases
    if (edgeCaseLabels[relativeDate]) {
        return edgeCaseLabels[relativeDate];
    }
    // Regex for relative date patterns
    const match = relativeDate.match(relativeDateRegex);
    if (!match) {
        return relativeDate; // Invalid input
    }
    const [, amount, unit] = match; // Extract amount and unit
    const value = parseInt(amount, 10); // Convert amount to integer
    // Generate labels for relative dates
    const unitMap = {
        d: "Day",
        w: "Week",
        m: "Month",
        y: "Year"
    };
    const unitLabel = unitMap[unit.toLowerCase()];
    const direction = value === 0 ? "This" : value > 0 ? "After" : "Before";
    const label = `${Math.abs(value)} ${unitLabel}${Math.abs(value) > 1 ? "s" : ""}`;
    return value === 0 ? `Current ${unitLabel}` : `${label} ${direction}`;
}
/**
 * Creates a promise that resolves after specified time
 *
 * @example
 * ```
 * await wait(1000); // Wait for 1 second
 * console.log('Done waiting');
 * ```
 */
export function wait(time) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((done, nope) => {
            setTimeout(() => {
                done('time is up');
            }, time);
        });
    });
}
/**
 * Converts old form structure to new section-based structure
 *
 * @example
 * ```
 * const newStructure = convertToNewFormStructure(oldFormFields);
 * ```
 */
export function convertToNewFormStructure(formStructure) {
    // Check if the formStructure is in the old format (flat array of fields)
    if (Array.isArray(formStructure) && formStructure.length > 0 && 'name' in formStructure[0]) {
        // Old structure detected
        const oldFormStructure = formStructure;
        // Convert old structure into new structure format
        const newFormStructure = [
            {
                fields: oldFormStructure.map(oldField => {
                    var _a;
                    return (Object.assign(Object.assign({}, oldField), { 
                        // Override specific conversions
                        type: (oldField === null || oldField === void 0 ? void 0 : oldField.type) === 'string' ? 'text' : oldField.type, value: (_a = oldField.value) !== null && _a !== void 0 ? _a : null }));
                }),
                columns: 1, // Default to 1 column
                title: '', // Add title if needed
                seperator: false, // Add separator if needed
            }
        ];
        return newFormStructure;
    }
    // If it's already in the new structure, return it as is
    return formStructure;
}
/**
 * @export
 * Spacing mode enumeration for UI components
 */
export var SpacingMode;
(function (SpacingMode) {
    SpacingMode["Compact"] = "compact";
    SpacingMode["Comfortable"] = "comfortable";
})(SpacingMode || (SpacingMode = {}));
/**
 * Gets the spacing mode from props or context
 *
 * @example
 * ```
 * const spacing = getSpacingMode(props, context);
 * ```
 */
export function getSpacingMode(props, context) {
    function validSpacingMode(mode) {
        var _a;
        return (_a = [...Object.values(SpacingMode)]) === null || _a === void 0 ? void 0 : _a.includes(mode);
    }
    if (hasValue(props === null || props === void 0 ? void 0 : props.spacingMode) && validSpacingMode(props.spacingMode))
        return props.spacingMode;
    if (hasValue(context === null || context === void 0 ? void 0 : context.spacingMode) && validSpacingMode(context.spacingMode))
        return context.spacingMode;
    return SpacingMode.Compact;
}
/**
 * Adjusts widget layout for mobile viewport
 *
 * @example
 * ```
 * const mobileLayout = adjustLayoutForMobile(widgets, 30);
 * ```
 */
export function adjustLayoutForMobile(items, fullWidth = 30) {
    // Sort items by x, y (left to right, top to bottom)
    const sortedItems = [...items].sort((a, b) => (a.layout.y - b.layout.y) || (a.layout.x - b.layout.x));
    // Recalculate w and h for mobile
    return sortedItems.map((item, i) => {
        const { w, h, y } = item.layout;
        const ratio = h / w;
        const scalledHeight = fullWidth * (ratio < 1 ? ratio * 2 : ratio);
        return Object.assign(Object.assign({}, item), { layout: Object.assign(Object.assign({}, item.layout), { w: fullWidth, h: scalledHeight, x: 0, y: y }) });
    });
}
/**
 * @export
 * Environment enumeration
 */
export var Environment;
(function (Environment) {
    Environment["Prod"] = "prod";
    Environment["Dev"] = "dev";
})(Environment || (Environment = {}));
/**
 * Checks if user is allowed based on groups and app roles
 *
 * @example
 * ```
 * const allowed = isUserAllowed(context, ['1'], ['UXP:canopenapp']);
 * ```
 */
export function isUserAllowed(context, userGroups = [], appRoles = []) {
    var _a;
    if (userGroups.length === 0 && appRoles.length === 0)
        return true;
    if (userGroups.includes((_a = context === null || context === void 0 ? void 0 : context.userDetails) === null || _a === void 0 ? void 0 : _a.UserGroupKey))
        return true;
    return appRoles.some(ar => {
        const [app, role] = ar.split(":", 2);
        return context.hasAppRole(app, role);
    });
}
/**
 * Whether the current user holds the System-app role that permits customising
 * page views and dashboards (the floating "Edit page" / "Edit dashboard" actions).
 */
export function canCustomisePagesAndDashboards(context) {
    return context.hasAppRole('System', 'cancustomisepagesanddashboards');
}
/**
 * @export
   * Parses a CSS size string and extracts value and unit
   * Supports any CSS unit (px, rem, em, vh, vw, %, etc.)
   *
   * @example
   * ```
   * const parsed = parseCSSUnit('5rem');
   * console.log(parsed); // { value: 5, unit: 'rem' }
   *
   * const parsed2 = parseCSSUnit('60px');
   * console.log(parsed2); // { value: 60, unit: 'px' }
   *
   * const parsed3 = parseCSSUnit('3.5em');
   * console.log(parsed3); // { value: 3.5, unit: 'em' }
   * ```
   */
export function parseCSSUnit(size) {
    if (!size || typeof size !== 'string')
        return null;
    const match = size.trim().match(/^(\d+(?:\.\d+)?)(px|rem|em|%|vh|vw|vmin|vmax|ch|ex)?$/);
    if (!match)
        return null;
    return {
        value: parseFloat(match[1]),
        unit: match[2] || 'px' // default to px if no unit specified
    };
}
/**
 * @export
 * Coerce a dimension to a CSS value: numbers become `px`, strings pass through.
 *
 * @example
 * ```
 * toCssValue(24);      // '24px'
 * toCssValue('2rem');  // '2rem'
 * ```
 */
export function toCssValue(value) {
    return typeof value === 'number' ? `${value}px` : value;
}
/**
 * @export
 * Extracts the first item from data, handling both arrays and objects.
 *
 * @param data - The data to extract from (can be array, object, or null/undefined)
 * @returns The first element if array, the object itself if object, or null if no data
 *
 * @example
 * ```
 * extractFirstItem([{ id: 1 }, { id: 2 }]); // Returns { id: 1 }
 * extractFirstItem({ id: 1 }); // Returns { id: 1 }
 * extractFirstItem([]); // Returns null
 * extractFirstItem(null); // Returns null
 * ```
 */
export function extractFirstItem(data) {
    if (data == null)
        return null;
    if (Array.isArray(data)) {
        return data.length > 0 ? data[0] : null;
    }
    return data;
}
